import { QueryClient, QueryFunction } from "@tanstack/react-query";

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

// CSRF token management
let cachedCSRFToken: string | null = null;

async function getCSRFToken(): Promise<string | null> {
  if (cachedCSRFToken) {
    return cachedCSRFToken;
  }
  
  try {
    const response = await fetch('/api/csrf-token', {
      credentials: 'include',
    });
    
    if (response.ok) {
      const data = await response.json();
      cachedCSRFToken = data.csrfToken;
      return cachedCSRFToken;
    }
  } catch (error) {
    console.warn('Failed to fetch CSRF token:', error);
  }
  
  return null;
}

// Clear cached CSRF token (e.g., on logout)
export function clearCSRFToken() {
  cachedCSRFToken = null;
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  let headers: Record<string, string> = data ? { "Content-Type": "application/json" } : {};

  // Add CSRF token for state-changing requests
  if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method.toUpperCase())) {
    const csrfToken = await getCSRFToken();
    if (csrfToken) {
      headers['x-csrf-token'] = csrfToken;
    }
  }

  // Add cache-busting headers to prevent 304 responses
  headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
  headers['Pragma'] = 'no-cache';

  const res = await fetch(url, {
    method,
    headers,
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
    cache: 'no-store',
  });

  // Handle CSRF token errors by clearing cache and retrying once
  if (res.status === 403 && ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method.toUpperCase())) {
    try {
      const errorData = await res.clone().json();
      if (errorData.code && errorData.code.startsWith('CSRF')) {
        console.warn('CSRF token error, refreshing token and retrying...');
        cachedCSRFToken = null; // Clear cached token
        const newCSRFToken = await getCSRFToken();
        if (newCSRFToken) {
          headers['x-csrf-token'] = newCSRFToken;
          const retryRes = await fetch(url, {
            method,
            headers,
            body: data ? JSON.stringify(data) : undefined,
            credentials: "include",
          });
          await throwIfResNotOk(retryRes);
          return retryRes;
        }
      }
    } catch (retryError) {
      console.warn('CSRF retry failed:', retryError);
    }
  }

  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const res = await fetch(queryKey[0] as string, {
      credentials: "include",
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
      },
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    const data = await res.json();
    
    // Compatibility shim: handle both array and {total, items} API response formats
    // This allows existing array-based consumers to continue working with new {total, items} format
    return Array.isArray(data) ? data : (data?.items ?? data);
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
